import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project, ProjectStatus } from '../../types/project';
import { useAuth } from '../../context/AuthContext';
import { projectService } from '../../services/projectService';
import ProjectDialog from './ProjectDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import { formatDateBR } from '../../utils/date';

const ProjectList: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth();
    
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const formatDate = (dateString: string | null | undefined) => formatDateBR(dateString);

    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        try {
            setLoading(true);
            const data = await projectService.getManagerProjects();
            setProjects(data);
        } catch (error) {
            console.error('Erro ao carregar projetos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNewProject = () => {
        setSelectedProject(undefined);
        setIsDialogOpen(true);
    };

    const handleEditProject = (project: Project) => {
        setSelectedProject(project);
        setIsDialogOpen(true);
    };

    const handleDeleteProject = (project: Project) => {
        setSelectedProject(project);
        setIsDeleteDialogOpen(true);
    };

    const handleSimulation = (projectId: number) => {
        navigate(`/gestor/projetos/${projectId}`);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setSelectedProject(undefined);
    };

    const handleDialogSave = async (project: Project) => {
        try {
            if (project.id) {
                await projectService.updateProject(project.id, project);
            } else {
                await projectService.createProject(project);
            }
            loadProjects();
            handleDialogClose();
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.error || 'Erro ao salvar projeto',
                severity: 'error'
            });
        }
    };

    const handleDeleteDialogClose = () => {
        setIsDeleteDialogOpen(false);
        setSelectedProject(undefined);
    };

    const handleDeleteConfirm = async () => {
        if (selectedProject) {
            try {
                await projectService.deleteProject(selectedProject.id);
                await loadProjects();
                setSnackbar({
                    open: true,
                    message: t('manager.projects.messages.deleteSuccess'),
                    severity: 'success'
                });
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: t('manager.projects.messages.error.delete'),
                    severity: 'error'
                });
            }
        }
        setIsDeleteDialogOpen(false);
        setSelectedProject(undefined);
    };

    if (loading) {
        return (
            <Box p={3}>
                <Typography>Carregando projetos...</Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    {t('manager.projects.title')}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleNewProject}
                >
                    {t('manager.projects.new')}
                </Button>
            </Box>

            {!loading && projects.length === 0 && (
                <Typography color="text.secondary">
                    Nenhum projeto ainda. Clique em "{t('manager.projects.new')}" para cadastrar uma oportunidade.
                </Typography>
            )}

            <Grid container spacing={3}>
                {projects.map((project) => (
                    <Grid item xs={12} sm={6} md={4} key={project.id}>
                        <Card>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                    {/* O nome abre o projeto (antes só o ícone de gráfico, sem rótulo, fazia isso) */}
                                    <Typography
                                        variant="h6"
                                        component="button"
                                        onClick={() => handleSimulation(project.id)}
                                        sx={{
                                            background: 'none', border: 'none', p: 0, textAlign: 'left',
                                            cursor: 'pointer', color: 'inherit', font: 'inherit',
                                            '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                                        }}
                                    >
                                        {project.name}
                                    </Typography>
                                    <Box sx={{ flexShrink: 0 }}>
                                        <IconButton
                                            size="small"
                                            aria-label={`Abrir ${project.name}`}
                                            title="Abrir projeto"
                                            onClick={() => handleSimulation(project.id)}
                                            color="primary"
                                        >
                                            <AssessmentIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            aria-label={`Editar ${project.name}`}
                                            title="Editar"
                                            onClick={() => handleEditProject(project)}
                                            color="primary"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            aria-label={`Excluir ${project.name}`}
                                            title="Excluir"
                                            onClick={() => handleDeleteProject(project)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Typography color="textSecondary" gutterBottom>
                                    {t('manager.projects.fields.status')}: {project.statusLabel}
                                </Typography>
                                {project.simulation && (
                                    <>
                                        <Typography color="textSecondary">
                                            {t('manager.projects.fields.startDate')}: {formatDate(project.simulation.start_date)}
                                        </Typography>
                                        <Typography color="textSecondary">
                                            {t('manager.projects.fields.endDate')}: {formatDate(project.simulation.end_date)}
                                        </Typography>
                                    </>
                                )}
                                {project.client && (
                                    <Typography color="textSecondary">
                                        {t('manager.projects.fields.client')}: {project.client.name}
                                    </Typography>
                                )}
                                {project.company && (
                                    <Typography color="textSecondary">
                                        {t('manager.projects.fields.company')}: {project.company.name}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <ProjectDialog
                open={isDialogOpen}
                project={selectedProject}
                onClose={handleDialogClose}
                onSave={handleDialogSave}
            />

            <ConfirmDialog
                open={isDeleteDialogOpen}
                title={t('manager.projects.delete')}
                message={`Deseja realmente excluir o projeto "${selectedProject?.name}"?`}
                onConfirm={handleDeleteConfirm}
                onClose={handleDeleteDialogClose}
            />
        </Box>
    );
};

export default ProjectList; 